import { type ComponentProps, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type MainButton } from '@components/ui/Button/Button';
import cn from 'classnames';

import { useOpenCardsCellMutation } from '@/api/queries/game';
import { playSound } from '@/common/lib/soundEffect';
import {
  type MiniGameDiceRollResponseType,
  type RollDiceResponseType,
} from '@/common/types/dice-game';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { setActiveBoard, setBoxReward } from '@/redux/reducers/diceGameReducer';
import { claimDiceGameBoxRewards } from '@/redux/reducers/userReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import { DiceGameContext } from '../context';
import { createActiveTileKeyframes } from '../lib';

import { useCardsGameCollectRewards } from './collect-rewards';
import { useMainDiceGame } from './main-dice-game';
import { useMiniDiceGame } from './mini-dice-game';

const useCardsGameButtonLogic = (): ComponentProps<typeof MainButton> => {
  const { t } = useTranslation();
  const { collectRewards, isLoading } = useCardsGameCollectRewards();
  const [, { isUninitialized }] = useOpenCardsCellMutation({
    fixedCacheKey: 'cards-mini-game-open-cell',
  });
  const cardsList = useAppSelector((state) => state.diceGame.miniGame?.board);

  const isClaimRewardActive =
    !!cardsList?.some((card) => card !== '*') || !isUninitialized;

  return {
    inactive: !isClaimRewardActive,
    isLoading: isLoading,
    disabled: isLoading || !isClaimRewardActive,
    className: cn(!isClaimRewardActive && '!backdrop-blur-0'),
    onClick: collectRewards,
    text: t('Collect'),
  };
};

const useMainGameButtonLogic = (): ComponentProps<typeof MainButton> => {
  // const {
  //   dice: { scope, animate },
  // } = useContext(DiceGameContext);
  const activeBoard = useAppSelector((state) => state.diceGame.activeBoard);
  const energyCount = useAppSelector((state) => state.diceGame.user?.energy);
  const {
    dice: { scope, animate },
    activeTileRef,
  } = useContext(DiceGameContext);
  const [count, setCount] = useState(1);
  const [isRollProcessActive, setIsRollProcessActive] = useState(false);
  const rollMultiplier = useAppSelector((state) => state.diceGame.rollPower);
  const skipAnimation = useAppSelector((state) => state.diceGame.skipAnimation);
  const tileAnimationSteps = useAppSelector(
    (state) => state.diceGame.tileAnimationSteps,
  );
  const { t } = useTranslation();
  const mainGame = useMainDiceGame();
  const miniGame = useMiniDiceGame({
    activeTileRef,
    tileAnimationSteps,
  });

  const [rollMainGameDice, mainGameRequestState] = mainGame.rollDiceMutation;
  const [rollMiniGameDice, miniGameRequestState] = miniGame.rollDiceMutation;

  const hasEnergyNotEnought = activeBoard === 'dice' && !energyCount;

  const boardPosition =
    activeBoard === 'miniGame'
      ? miniGame.boardPosition
      : mainGame.boardPosition;

  const diceCount =
    activeBoard === 'dice' ? mainGame.diceCount : miniGame.diceCount;

  const rollMainGameRequest = async (): Promise<RollDiceResponseType> =>
    await rollMainGameDice(rollMultiplier).unwrap();
  const rollMiniGameRequest = async (): Promise<MiniGameDiceRollResponseType> =>
    await rollMiniGameDice().unwrap();
  const responseHandler = async (
    response: RollDiceResponseType | MiniGameDiceRollResponseType,
  ): Promise<void> => {
    try {
      if ('energy' in response) {
        await mainGame.responseHandler(response);
      } else {
        await miniGame.responseHandler(response);
      }
    } catch (error) {
      console.error(error);
      showToast(t('Something went wrong, please try again'));
    }
  };

  const finalAnimationVariants: Record<
    number,
    { rotateX: number; rotateY: number }
  > = {
    1: { rotateX: 0, rotateY: 0 },
    2: { rotateX: -90, rotateY: 0 },
    3: { rotateX: 0, rotateY: 90 },
    4: { rotateX: 0, rotateY: -90 },
    5: { rotateX: 90, rotateY: 0 },
    6: { rotateX: 180, rotateY: 0 },
  };

  const rollDice = async (
    rollRequest: typeof rollMainGameRequest | typeof rollMiniGameRequest,
  ): Promise<void> => {
    try {
      playSound('roll/dice');
      setIsRollProcessActive(true);
      const currentPosition = boardPosition || 0;
      const rollResult = await rollRequest();
      const diceRollResult = rollResult.dice;
      const kof = finalAnimationVariants[diceRollResult];

      const finalAnimation = {
        rotateX: kof.rotateX + count * 360,
        rotateY: kof.rotateY + count * 720,
        rotateZ: count * 360,
      };
      setCount((state) => state + 1);

      await animate(scope.current, finalAnimation, {
        duration: skipAnimation ? 0 : 1,
        ease: 'easeInOut',
      });
      const animation = createActiveTileKeyframes(
        currentPosition,
        diceRollResult,
        tileAnimationSteps,
      );
      await activeTileRef.current?.animate(animation, {
        duration: skipAnimation ? 0 : 0.4 * diceRollResult,
      });
      await responseHandler(rollResult);
    } catch (error) {
      console.error(error);
      showToast(t('Something went wrong, please try again'));
    } finally {
      setIsRollProcessActive(false);
    }
  };

  return {
    text: t('Roll'),
    isLoading: mainGameRequestState.isLoading || miniGameRequestState.isLoading,
    disabled:
      !diceCount ||
      hasEnergyNotEnought ||
      mainGameRequestState.isLoading ||
      miniGameRequestState.isLoading ||
      isRollProcessActive,
    inactive: !diceCount || hasEnergyNotEnought,
    onClick: async () =>
      await rollDice(
        activeBoard === 'miniGame' ? rollMiniGameRequest : rollMainGameRequest,
      ),
  };
};

const useClaimBoxRewardButtonLogic = (): ComponentProps<typeof MainButton> => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const boxReward = useAppSelector((state) => state.diceGame.boxReward);

  return {
    text: t('Collect'),
    className: 'relative z-[2] !mt-0',
    onClick: (): void => {
      if (boxReward) {
        dispatch(claimDiceGameBoxRewards(boxReward));
      }
      dispatch(setActiveBoard('dice'));
      dispatch(setBoxReward(null));
    },
  };
};

export {
  useCardsGameButtonLogic,
  useClaimBoxRewardButtonLogic,
  useMainGameButtonLogic,
};
